import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SEVERITY = {
  haute: { label: "Haute", cls: "bg-terra/15 text-terra border-terra/30" },
  moyenne: { label: "Moyenne", cls: "bg-amber-deep/15 text-amber-deep border-amber-deep/30" },
  basse: { label: "Basse", cls: "bg-sage/20 text-sage-dark border-sage/40" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function DocEditor({ doc, onSaved }) {
  const [title, setTitle] = useState(doc.title);
  const [body, setBody] = useState(doc.body);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(doc.last_review);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/legal/${doc.doc_id}`, { title: title.trim(), body: body.trim() });
      toast.success("Document enregistré");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runReview = async () => {
    setReviewing(true);
    try {
      const { data } = await api.post(`/admin/legal/${doc.doc_id}/review`);
      setReview(data);
      toast.success(`Revue IA terminée : ${data.findings.length} point(s) signalé(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "La revue IA a échoué");
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div data-testid={`legal-editor-${doc.doc_id}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <input
            data-testid={`legal-title-${doc.doc_id}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full min-w-0 border-none bg-transparent font-serif text-2xl font-semibold text-bark outline-none focus:ring-0"
          />
          <p className="mt-1 text-xs text-stone-mute">
            Dernière mise à jour : {fmtDate(doc.updated_at)} — par {doc.updated_by || "—"}
          </p>
        </div>
      </div>

      <textarea
        data-testid={`legal-body-${doc.doc_id}`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={16}
        className="w-full rounded-xl border border-amber-800/20 bg-cream/40 p-4 font-mono text-sm leading-relaxed text-bark outline-none transition-colors focus:border-amber-deep"
      />
      <p className="mt-2 text-xs text-stone-mute">
        Astuce : commencez une ligne par <code className="rounded bg-sand px-1">## </code> pour créer un titre de section. Séparez les paragraphes par une ligne vide.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid={`legal-save-${doc.doc_id}`}
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
        </button>
        <button
          data-testid={`legal-review-${doc.doc_id}`}
          onClick={runReview}
          disabled={reviewing}
          className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60"
        >
          {reviewing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Lancer la revue IA
        </button>
      </div>

      {review && (
        <div data-testid={`legal-review-result-${doc.doc_id}`} className="mt-5 rounded-xl border border-amber-800/15 bg-cream/50 p-4">
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-amber-deep" />
            <p className="text-sm font-semibold text-bark">Veille IA — {fmtDate(review.date)}</p>
          </div>
          {review.summary && <p className="mt-2 text-sm text-bark-soft">{review.summary}</p>}
          {review.findings?.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {review.findings.map((f, i) => {
                const sev = SEVERITY[f.severity] || SEVERITY.moyenne;
                return (
                  <li key={i} className="flex items-start gap-2 text-sm text-bark-soft">
                    <span className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${sev.cls}`}>{sev.label}</span>
                    <span>{f.point}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-sage-dark">Aucun point bloquant relevé.</p>
          )}
          <p className="mt-3 text-xs italic text-stone-mute">
            Analyse générée par IA à titre indicatif — elle ne remplace pas l'avis d'un professionnel du droit.
          </p>
        </div>
      )}
    </div>
  );
}

export default function AdminLegal() {
  const [docs, setDocs] = useState(null);

  const load = () => api.get("/admin/legal").then(({ data }) => setDocs(data));

  useEffect(() => {
    load();
  }, []);

  const handleSaved = (updated) =>
    setDocs((prev) => prev.map((d) => (d.doc_id === updated.doc_id ? { ...d, ...updated } : d)));

  if (!docs) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-legal-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-bark">Mentions & RGPD</h1>
        <p className="mt-1 text-sm text-bark-soft">
          Éditez vos documents légaux. La veille IA s'exécute automatiquement chaque mois et signale les points à vérifier ;
          vous pouvez aussi la lancer à la demande.
        </p>
      </div>
      {docs.map((doc) => (
        <DocEditor key={doc.doc_id} doc={doc} onSaved={handleSaved} />
      ))}
    </div>
  );
}
